import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Chip,
  Grid,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from "@mui/material";
import {
  Close,
  Email,
  Person,
  AdminPanelSettings,
  CalendarMonth,
  Login,
  Security,
  CheckCircle,
  Cancel,
  Edit,
} from "@mui/icons-material";
import { USER_ROLES } from "../../utils/roleUtils";

const UserDetailsDialog = ({ open, onClose, user, onEdit }) => {
  if (!user) return null;

  const getInitials = () => {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return user.email.substring(0, 2).toUpperCase();
  };

  const getRoleColor = (role) => {
    switch (role) {
      case USER_ROLES.ADMIN:
        return "primary";
      default:
        return "default";
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">User Details</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* User Header */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              margin: "0 auto",
              mb: 2,
              bgcolor: user.isActive ? "primary.main" : "grey.400",
              fontSize: 32,
            }}
          >
            {getInitials()}
          </Avatar>
          <Typography variant="h5" gutterBottom>
            {user.firstName} {user.lastName}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {user.email}
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 1 }}
          >
            <Chip
              label={user.role.replace("_", " ")}
              color={getRoleColor(user.role)}
              size="small"
              icon={<AdminPanelSettings />}
            />
            <Chip
              label={user.isActive ? "Active" : "Inactive"}
              color={user.isActive ? "success" : "default"}
              size="small"
              icon={user.isActive ? <CheckCircle /> : <Cancel />}
            />
            {user.emailVerified && (
              <Chip
                label="Email Verified"
                color="success"
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* User Information Grid */}
        <Grid container spacing={3}>
          {/* Account Information */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Account Information
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Person fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Full Name"
                      secondary={`${user.firstName} ${user.lastName}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Email fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email Address"
                      secondary={user.email}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <AdminPanelSettings fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Role"
                      secondary={user.role.replace("_", " ")}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Security fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Account Status"
                      secondary={user.isActive ? "Active" : "Inactive"}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Security fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Plan"
                      secondary={user.planName || "No Plan"}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Activity Information */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Activity & Dates
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CalendarMonth fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Account Created"
                      secondary={formatDate(user.createdAt)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Login fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Last Login"
                      secondary={formatDate(user.lastLoginAt) || "Never"}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CalendarMonth fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Last Updated"
                      secondary={formatDate(user.updatedAt)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email Verified"
                      secondary={user.emailVerified ? "Yes" : "No"}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Statistics */}
          <Grid size={{ xs: 12 }}>
            <Card variant="outlined" sx={{ bgcolor: "background.default" }}>
              <CardContent>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  User Statistics
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 4 }}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h4" color="primary">
                        {user.sitesCount || user._count?.sites || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Sites
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h4" color="success.main">
                        {user.activeDeployments || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Active Deployments
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h4" color="info.main">
                        {user.totalBackups || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Backups
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        {onEdit && (
          <Button
            onClick={() => {
              onEdit(user);
              onClose();
            }}
            variant="contained"
            startIcon={<Edit />}
          >
            Edit User
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UserDetailsDialog;
